import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const Button = ({
  children,
  variant = "primary",
  href,
  onClick,
  type = "button",
  className = "",
  disabled = false,
  size = "medium",
  fullWidth = false,
  icon = null,
  iconPosition = "left",
  animate = true,
  ...props
}) => {
  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    borderRadius: "8px",
    fontWeight: "600",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: disabled ? "not-allowed" : "pointer",
    border: "none",
    textAlign: "center",
    opacity: disabled ? 0.6 : 1,
    minHeight: "44px",
    minWidth: "44px",
    width: fullWidth ? "100%" : "auto",
    fontFamily: "'Inter', sans-serif",
    ...props.style,
  };

  const sizeStyles = {
    small: {
      padding: "0.375rem 0.875rem",
      fontSize: "0.75rem",
      minHeight: "36px",
    },
    medium: { padding: "0.625rem 1.25rem", fontSize: "0.875rem" },
    large: { padding: "0.875rem 2rem", fontSize: "1rem" },
  };

  const variantStyles = {
    primary: {
      background: "#D9A321",
      color: "#FFFFFF",
      "&:hover:not(:disabled)": {
        background: "#B8860B",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 15px rgba(217, 163, 33, 0.35)",
      },
    },
    secondary: {
      background: "#006B2E",
      color: "#FFFFFF",
      "&:hover:not(:disabled)": {
        background: "#014421",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 15px rgba(0, 107, 46, 0.35)",
      },
    },
    outline: {
      background: "transparent",
      border: "2px solid #006B2E",
      color: "#006B2E",
      "&:hover:not(:disabled)": {
        background: "#006B2E",
        color: "#FFFFFF",
        transform: "translateY(-2px)",
      },
    },
    "outline-gold": {
      background: "transparent",
      border: "2px solid #D9A321",
      color: "#D9A321",
      "&:hover:not(:disabled)": {
        background: "#D9A321",
        color: "#FFFFFF",
        transform: "translateY(-2px)",
      },
    },
    danger: {
      background: "#dc2626",
      color: "#FFFFFF",
      "&:hover:not(:disabled)": {
        background: "#b91c1c",
        transform: "translateY(-2px)",
      },
    },
    ghost: {
      background: "transparent",
      color: "#5A5A5A",
      "&:hover:not(:disabled)": {
        background: "rgba(0, 0, 0, 0.05)",
      },
    },
  };

  const combinedStyle = {
    ...baseStyle,
    ...(sizeStyles[size] || sizeStyles.medium),
    ...(variantStyles[variant] || variantStyles.primary),
  };

  const cleanStyle = { ...combinedStyle };
  delete cleanStyle["&:hover:not(:disabled)"];

  const handleClick = (e) => {
    if (disabled) return;
    if (onClick) {
      onClick(e);
    }
  };

  const content = (
    <>
      {icon && iconPosition === "left" && (
        <span className="btn-icon">{icon}</span>
      )}
      {children}
      {icon && iconPosition === "right" && (
        <span className="btn-icon">{icon}</span>
      )}
    </>
  );

  const buttonElement = href ? (
    <Link
      to={href}
      style={cleanStyle}
      className={`btn btn-${variant} btn-${size} ${fullWidth ? "btn-full" : ""} ${className}`}
      {...props}
    >
      {content}
    </Link>
  ) : (
    <button
      type={type}
      onClick={handleClick}
      style={cleanStyle}
      className={`btn btn-${variant} btn-${size} ${fullWidth ? "btn-full" : ""} ${className}`}
      disabled={disabled}
      {...props}
    >
      {content}
    </button>
  );

  if (animate && !disabled) {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        transition={{ duration: 0.2 }}
        style={{ display: "inline-flex", width: fullWidth ? "100%" : "auto" }}
      >
        {buttonElement}
      </motion.div>
    );
  }

  return buttonElement;
};

export default Button;
